import { createStore } from '@stencil/store';
import { Calendar, CalendarDoc, CompanyDoc, Constants, CostCodeDoc, DocUtils, FileDoc, PersonDoc, ProjectDoc } from 'common';
import { TeamDoc } from '../service/PeopleService';

export type ProjectFilesStatus = 'idle' | 'syncing' | 'ready';

export interface ProjectStoreState {
  projectId: string | null;
  project: ProjectDoc | null;
  company: CompanyDoc;
  calendar: Calendar | null;
  people: PersonDoc[];
  /** Teams live in the same DB as people and are loaded with them. */
  teams: TeamDoc[];
  files: FileDoc[];
  peopleLoading: boolean;
  /** Initial remote files pull progress, 0–1. */
  filesProgress: number;
  filesStatus: ProjectFilesStatus;
  /** Raw cost_code + division docs, deleted docs excluded. */
  costCodes: CostCodeDoc[];
  costCodesLoading: boolean;
}

const initialStoreState: ProjectStoreState = {
  projectId: null,
  project: null,
  company: Constants.DEFAULT_COMPANY,
  calendar: null,
  people: [],
  teams: [],
  files: [],
  peopleLoading: false,
  filesProgress: 0,
  filesStatus: 'idle',
  costCodes: [],
  costCodesLoading: false,
};

export const { state: projectState, onChange: projectStoreOnChange, dispose: disposeProjectStore } = createStore<ProjectStoreState>(initialStoreState);

export const setProjectId = (projectId: string) => {
  projectState.projectId = projectId;
};

export const setProjectDoc = (projectId: string, project: ProjectDoc) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.project = project;
};

export const setCompanyDoc = (projectId: string, company: CompanyDoc | null) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.company = company ?? Constants.DEFAULT_COMPANY;
};

export const applyProjectPeopleDocs = (projectId: string, docs: any[]) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.people = docs.filter(doc => doc.type !== 'team');
  projectState.teams = docs.filter(doc => doc.type === 'team');
  projectState.peopleLoading = false;
};

export const setProjectPeopleLoading = (loading: boolean) => {
  projectState.peopleLoading = loading;
};

export const beginProjectFilesSync = (projectId: string) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.files = [];
  projectState.filesProgress = 0;
  projectState.filesStatus = 'syncing';
};

export const applyProjectFileDocs = (projectId: string, docs: FileDoc[]) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.files = docs.filter(doc => !doc.deleted_date);
};

export const setProjectFilesProgress = (projectId: string, progress: number) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.filesProgress = Math.min(1, Math.max(0, progress));
  if (projectState.filesStatus === 'idle') {
    projectState.filesStatus = 'syncing';
  }
  if (projectState.filesProgress >= 1) {
    projectState.filesStatus = 'ready';
  }
};

export const setProjectFilesReady = (projectId: string) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.filesProgress = 1;
  projectState.filesStatus = 'ready';
};

export const applyCalendarDocs = (projectId: string, docs: CalendarDoc[]) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  const defaultCalendarId = projectState.project?.default_calendar_unique_id;
  const defaultCalendarDoc = docs.find(doc => doc.unique_id === defaultCalendarId);
  projectState.calendar = defaultCalendarDoc ? new Calendar(defaultCalendarDoc) : Calendar.getDefaultCalendar();
};

export const applyProjectCostCodeDocs = (projectId: string, docs: CostCodeDoc[]) => {
  if (projectState.projectId != null && projectState.projectId !== projectId) return;
  projectState.costCodes = DocUtils.excludeDeleted(docs);
  projectState.costCodesLoading = false;
};

export const setProjectCostCodesLoading = (loading: boolean) => {
  projectState.costCodesLoading = loading;
};

export const resetProjectStore = () => {
  projectState.projectId = null;
  projectState.project = null;
  projectState.company = Constants.DEFAULT_COMPANY;
  projectState.calendar = null;
  projectState.people = [];
  projectState.teams = [];
  projectState.files = [];
  projectState.peopleLoading = false;
  projectState.filesProgress = 0;
  projectState.filesStatus = 'idle';
  projectState.costCodes = [];
  projectState.costCodesLoading = false;
};
